import { cva } from 'class-variance-authority'

/**
 * The shared shell for anything a user types or picks in.
 *
 * Input, Textarea and Select trigger all render the same box, so they must not
 * each describe it. A change to focus treatment lands in one place.
 *
 * The focus rule reads `:focus`, not `:focus-visible`.
 *
 * A browser matches `:focus-visible` on a text input however the focus
 * arrived, and on a button only when the keyboard brought it. Select and the
 * PhoneInput country picker render buttons, so a mouse click left them with no
 * ring while the Input beside them drew one. The two controls do the same job,
 * and the ring says "your typing lands here", which is as true after a click
 * as after a Tab.
 *
 * `data-popup-open` carries the same treatment. Opening a Select moves focus
 * into its list, so the trigger loses `:focus` at the moment the user starts
 * working in it, and the ring blinked out as the menu appeared. Base UI marks
 * the open trigger with that attribute, so the control keeps the ring for as
 * long as its overlay is on screen.
 */
export const controlVariants = cva(
    [
        'bg-background text-foreground border-border w-full rounded-aria-md border',
        'placeholder:text-foreground-disabled',
        'transition-colors',
        'focus:border-border-accent focus:ring-ring focus:ring-1 focus:outline-none',
        'data-popup-open:border-border-accent data-popup-open:ring-ring data-popup-open:ring-1',
        'disabled:bg-background-disabled disabled:text-foreground-disabled disabled:cursor-not-allowed',
        'aria-[invalid=true]:border-border-danger-strong aria-[invalid=true]:ring-border-danger-strong aria-[invalid=true]:ring-1'
    ],
    {
        variants: {
            size: {
                small: 'h-6 px-2 text-aria-xs',
                medium: 'h-8 px-3 text-aria-base',
                large: 'h-10 px-4 text-aria-lg'
            },
            /**
             * Room for a marker or a button inside the control's own padding box.
             *
             * The padding belongs to the control, so it lives here. The marker's
             * own geometry belongs to the marker, so it lives in adornment.ts.
             * Four components each carried the whole table before, and the one
             * in combobox.tsx said "mirroring SearchInput" in a comment, which
             * is the note a copy leaves behind.
             */
            hasStartAdornment: { true: '', false: '' },
            /**
             * `double` is two markers at the end, a clear button beside a
             * calendar button. Twice the room, so the value never runs under
             * either of them.
             */
            hasEndAdornment: { true: '', false: '', double: '' }
        },
        /*
         * Physical `pl`/`pr` rather than logical `ps`/`pe`: Tailwind emits
         * `padding-inline` after `padding-inline-start`, so a logical utility
         * would lose to the shell's own `px`.
         *
         * Compound rather than a variant of its own, because the room needed
         * depends on both the side and the size. cva emits these last, which is
         * what lets the merge in each component resolve them over `px-*`.
         */
        compoundVariants: [
            { size: 'small', hasStartAdornment: true, class: 'pl-7' },
            { size: 'medium', hasStartAdornment: true, class: 'pl-9' },
            { size: 'large', hasStartAdornment: true, class: 'pl-10' },
            { size: 'small', hasEndAdornment: true, class: 'pr-7' },
            { size: 'medium', hasEndAdornment: true, class: 'pr-9' },
            { size: 'large', hasEndAdornment: true, class: 'pr-10' },
            { size: 'small', hasEndAdornment: 'double', class: 'pr-14' },
            { size: 'medium', hasEndAdornment: 'double', class: 'pr-18' },
            { size: 'large', hasEndAdornment: 'double', class: 'pr-20' }
        ],
        defaultVariants: { size: 'medium', hasStartAdornment: false, hasEndAdornment: false }
    }
)

/** The sizes a control comes in. Adornments key off the same names. */
export type ControlSize = 'small' | 'medium' | 'large'
